import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Basketball, SoccerBall, MapPin, CalendarBlank, Users, Play } from "@phosphor-icons/react";
import TeaserIntro from "../components/TeaserIntro";
import StayInformed from "../components/StayInformed";
import { REPORT_MODE } from "../config";

const HERO_BG =
  "https://customer-assets.emergentagent.com/job_festurban-live/artifacts/fyeuxroq_URBAN%20FEST%20OK.jpg";
const BASKET_IMG =
  "https://images.unsplash.com/photo-1631016033901-13fbea8620a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxzdHJlZXQlMjBiYXNrZXRiYWxsJTIwY291cnQlMjBuaWdodHxlbnwwfHx8fDE3NzY5MjkwMzh8MA&ixlib=rb-4.1.0&q=85";
const FOOT_IMG =
  "https://images.unsplash.com/photo-1518377230152-b4203a060598?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxzdHJlZXQlMjBmb290YmFsbCUyMHVyYmFufGVufDB8fHx8MTc3NjkyOTAzOHww&ixlib=rb-4.1.0&q=85";
const CROWD_IMG =
  "https://images.pexels.com/photos/31855482/pexels-photo-31855482.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const POSTER_IMG =
  "https://customer-assets.emergentagent.com/job_festurban-live/artifacts/2gunmg93_affiche%20urban%20fest.png";
const AIJ_LOGO =
  "https://customer-assets.emergentagent.com/job_festurban-live/artifacts/l19s6upi_logo%20action%20impact%20jeunesse.png";

export default function Home() {
  const [info, setInfo] = useState(null);
  const [partners, setPartners] = useState([]);
  const [headliners, setHeadliners] = useState([]);
  const [showTeaser, setShowTeaser] = useState(() => {
    try {
      return sessionStorage.getItem("uf_teaser_seen") !== "1";
    } catch {
      return true;
    }
  });

  const closeTeaser = () => {
    try {
      sessionStorage.setItem("uf_teaser_seen", "1");
    } catch {}
    setShowTeaser(false);
  };
  const replayTeaser = () => setShowTeaser(true);

  useEffect(() => {
    api.get("/info").then((r) => setInfo(r.data)).catch(() => {});
    api.get("/partners").then((r) => setPartners(r.data)).catch(() => {});
    api.get("/headliners").then((r) => setHeadliners(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (showTeaser) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [showTeaser]);

  return (
    <div data-testid="home-page">
      {showTeaser && <TeaserIntro onClose={closeTeaser} />}

      {/* Replay teaser floating button */}
      <button
        onClick={replayTeaser}
        className="fixed bottom-6 left-6 z-40 group flex items-center gap-2 px-4 py-2 rounded-full border border-[#FF6B1A]/40 bg-black/70 backdrop-blur text-[#FF6B1A] text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#FF6B1A] hover:text-black transition-colors"
        data-testid="replay-teaser-btn"
      >
        <Play size={14} weight="fill" />
        Revoir le teaser
      </button>

      {/* HERO */}
      <section className="relative min-h-[92vh] overflow-hidden flex items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, rgba(255,107,26,0.25) 0%, rgba(0,0,0,0.85) 70%)" }} />
        <div className="noise-overlay" />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-8 py-20 text-center">
          <div className="pill mb-8 fade-up" data-testid="hero-pill">
            <MapPin size={16} weight="bold" />
            <span>La Roche-sur-Yon · Jean-Yole</span>
          </div>

          <h1 className="font-display text-6xl sm:text-8xl lg:text-[9rem] leading-[0.9] tracking-tighter fade-up">
            URBAN<br />
            <span className="bg-gradient-to-r from-[#FFC32E] via-[#FF6B1A] to-[#E63946] bg-clip-text text-transparent" style={{ filter: "drop-shadow(0 0 40px rgba(255,107,26,0.45))" }}>FEST 2026</span>
          </h1>

          <p className="mt-8 max-w-2xl mx-auto text-white/85 text-lg sm:text-xl leading-relaxed">
            Le tournoi urbain le plus chaud de Vendée. Basket 3v3, Football 5v5, concert, danse, graff :{" "}
            {REPORT_MODE ? (
              <span className="text-[#FFB800] font-bold">nouvelle date à venir</span>
            ) : (
              "29 & 30 août"
            )}
            , deux jours pour vibrer.
          </p>

          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/programme" className="btn-brutal" data-testid="hero-programme-btn">
              <CalendarBlank size={18} weight="bold" /> Voir le programme
            </Link>
            {!REPORT_MODE && (
              <Link to="/inscription" className="btn-brutal btn-brutal-ghost" data-testid="hero-register-btn">
                <Users size={18} weight="bold" /> Inscrire mon équipe
              </Link>
            )}
          </div>
          {REPORT_MODE && (
            <p className="mt-6 text-sm uppercase tracking-widest font-bold text-[#FFB800]" data-testid="hero-report-note">
              ⚠ Événement reporté — inscriptions suspendues jusqu'à l'annonce de la nouvelle date
            </p>
          )}
        </div>

        {/* Floating glow line */}
        <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#FF6B1A]/60 to-transparent" />
      </section>

      {/* STAY INFORMED (report mode) */}
      {REPORT_MODE && <StayInformed />}

      {/* STATS BIG NUMBERS */}
      <section className="border-b border-white/5 bg-black" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-14 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { n: "28", l: "Équipes" },
            { n: "02", l: "Disciplines" },
            { n: "55+", l: "Matchs" },
            { n: "2", l: "Jours de show" },
          ].map((s, i) => (
            <div key={i} className="space-y-1" data-testid={`stat-${i}`}>
              <p className="font-display text-6xl sm:text-7xl bg-gradient-to-r from-[#FFC32E] via-[#FF6B1A] to-[#E63946] bg-clip-text text-transparent" style={{ filter: "drop-shadow(0 0 30px rgba(255,107,26,0.35))" }}>
                {s.n}
              </p>
              <p className="text-xs tracking-[0.3em] uppercase text-white/60">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-[#FF6B1A] text-black py-5 overflow-hidden">
        <Marquee speed={50} gradient={false} autoFill>
          <span className="font-display text-3xl sm:text-5xl px-8">
            BASKET 3V3 ★ FOOTBALL 5V5 ★ CONCERT ★ DANSE ★ GRAFF ★ TOMBOLA ★ {REPORT_MODE ? "REPORTÉ — NOUVELLE DATE À VENIR" : "29-30 AOÛT 2026"} ★&nbsp;
          </span>
        </Marquee>
      </section>

      {/* AFFICHE OFFICIELLE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-24" data-testid="affiche-section">
        <p className="text-xs tracking-[0.3em] uppercase text-[#FF6B1A] mb-4">Infos clés</p>
        <h2 className="font-display text-4xl sm:text-6xl leading-none mb-12">RENDEZ-VOUS</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          <div className="uf-card p-6 border-l-4 border-l-[#FF6B1A]">
            <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-3">Quand</p>
            <p className="font-display text-3xl sm:text-4xl">29 & 30</p>
            <p className="font-display text-2xl text-[#FF6B1A]">AOÛT 2026</p>
            <p className="text-white/60 text-sm mt-2">Deux jours non-stop</p>
          </div>
          <div className="uf-card p-6 border-l-4 border-l-[#FF6B1A]">
            <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-3">Où</p>
            <p className="font-display text-3xl sm:text-4xl">JEAN-YOLE</p>
            <p className="font-display text-2xl text-[#FF6B1A]">LA ROCHE-SUR-YON</p>
            <p className="text-white/60 text-sm mt-2">Vendée (85)</p>
          </div>
          <div className="uf-card p-6 border-l-4 border-l-[#FF6B1A]">
            <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-3">Quoi</p>
            <p className="font-display text-3xl sm:text-4xl">BASKET 3V3</p>
            <p className="font-display text-2xl text-[#FF6B1A]">FOOTBALL 5V5</p>
            <p className="text-white/60 text-sm mt-2">+ animations culturelles</p>
          </div>
        </div>

        <div className="uf-card p-6 sm:p-8">
          <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-4">En marge du sport</p>
          <div className="flex gap-3 flex-wrap">
            {["CONCERT", "DANSE", "GRAFF", "TOMBOLA", "RESTAURATION"].map((a) => (
              <span key={a} className="text-xs sm:text-sm tracking-[0.2em] uppercase font-bold border-2 border-[#FF6B1A] text-[#FF6B1A] px-4 py-2">
                {a}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CONCEPT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <p className="text-xs tracking-[0.3em] uppercase text-[#FF6B1A] mb-4">01 / Le concept</p>
          </div>
          <div className="lg:col-span-8 space-y-6 text-white/80 text-lg leading-relaxed">
            <p>
              <span className="text-white font-bold">Urban Fest 2026</span> c'est l'événement qui rassemble les jeunes
              de tous les quartiers de La Roche-sur-Yon autour du sport, de la culture et du vivre-ensemble.
            </p>
            <p>
              Porté par <span className="text-[#FF6B1A] font-bold">Action Impact Jeunesse</span>, le tournoi met en lumière
              les talents locaux à travers deux disciplines phares, des performances artistiques, du freestyle et une
              ambiance 100% urbaine.
            </p>
            <div className="grid grid-cols-3 gap-4 pt-4">
              {[
                { k: "2", v: "Jours" },
                { k: "16+", v: "Équipes" },
                { k: "∞", v: "Énergie" },
              ].map((s, i) => (
                <div key={i} className="uf-card p-6">
                  <p className="font-display text-5xl text-[#FF6B1A]">{s.k}</p>
                  <p className="text-xs tracking-widest uppercase text-white/50 mt-2">{s.v}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DISCIPLINES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
        <p className="text-xs tracking-[0.3em] uppercase text-[#FF6B1A] mb-4">02 / Disciplines</p>
        <h2 className="font-display text-4xl sm:text-6xl leading-none mb-12">DEUX TERRAINS.<br />UN SEUL OBJECTIF.</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <DisciplineCard
            title="Basket 3x3"
            subtitle="Street game"
            img={BASKET_IMG}
            features={["Matchs rapides", "Playground vibe", "Finale dimanche"]}
          />
          <DisciplineCard
            title="Football 5x5"
            subtitle="City cage"
            img={FOOT_IMG}
            features={["Équipes de quartier", "Phase de poules", "Titre en jeu"]}
          />
        </div>
      </section>

      {/* PROGRAMME PREVIEW */}
      {info && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 py-24">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-[#FF6B1A] mb-4">03 / Programme</p>
              <h2 className="font-display text-4xl sm:text-6xl leading-none">2 JOURS DE SHOW</h2>
            </div>
            <Link to="/programme" className="btn-brutal btn-brutal-ghost" data-testid="programme-cta">
              Voir le détail
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <DayPreview title="Samedi" items={info.schedule.samedi.slice(0, 4)} />
            <DayPreview title="Dimanche" items={info.schedule.dimanche.slice(0, 4)} />
          </div>
        </section>
      )}

      {/* COMMUNITY */}
      <section
        className="relative py-32 overflow-hidden"
        style={{ backgroundImage: `url(${CROWD_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-black/75" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 text-center">
          <p className="text-xs tracking-[0.3em] uppercase text-[#FF6B1A] mb-4">04 / Communauté</p>
          <h2 className="font-display text-5xl sm:text-7xl leading-none max-w-4xl mx-auto">
            UN QUARTIER, UNE ÉQUIPE, UNE VILLE ENTIÈRE.
          </h2>
          <p className="text-white/80 text-lg mt-8 max-w-2xl mx-auto">
            Représente ton quartier. Affronte les autres. Fais gagner ta ville. Le fair-play est aussi un trophée.
          </p>
          <div className="mt-10">
            {REPORT_MODE ? (
              <p className="text-sm uppercase tracking-widest font-bold text-[#FFB800]" data-testid="community-report-note">
                Inscriptions suspendues — rendez-vous à la nouvelle date !
              </p>
            ) : (
              <Link to="/inscription" className="btn-brutal" data-testid="community-cta">
                Rejoindre le tournoi
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* HEADLINERS */}
      {headliners.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-8 py-24" data-testid="headliners-section">
          <p className="text-xs tracking-[0.3em] uppercase text-[#FF6B1A] mb-4">Têtes d'affiche</p>
          <h2 className="font-display text-4xl sm:text-6xl leading-none mb-4">LE LINE-UP</h2>
          <p className="text-white/60 max-w-2xl mb-12 text-lg">
            Les artistes, DJ, freestylers et invités qui feront vibrer Urban Fest 2026.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="headliners-grid">
            {headliners.map((h) => (
              <article
                key={h.id}
                className="group relative overflow-hidden rounded-xl border border-white/10 hover:border-[#FF6B1A] transition-all duration-300 bg-[#0e0e0e]"
                data-testid={`headliner-${h.id}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={h.image_base64}
                    alt={h.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                  {h.role && (
                    <span className="absolute top-4 left-4 pill !py-1 !px-3 !text-[10px]">
                      {h.role}
                    </span>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-display text-2xl sm:text-3xl leading-tight">
                    {h.name}
                  </h3>
                  {h.description && (
                    <p className="text-white/60 text-sm mt-2 line-clamp-3">{h.description}</p>
                  )}
                  {h.instagram && (
                    <a
                      href={h.instagram.startsWith("http") ? h.instagram : `https://instagram.com/${h.instagram.replace(/^@/, "")}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#FF6B1A] hover:underline mt-3"
                    >
                      Instagram →
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* PARTNERS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-24">
        <p className="text-xs tracking-[0.3em] uppercase text-[#FF6B1A] mb-4">05 / Partenaires</p>
        <h2 className="font-display text-4xl sm:text-6xl leading-none mb-12">ILS SOUTIENNENT LE MOUVEMENT</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="partners-grid">
          <a
            href="https://www.actionimpactjeunesse.com"
            target="_blank"
            rel="noreferrer"
            className="uf-card p-6 flex items-center justify-center hover:border-[#FF6B1A] transition-colors min-h-[140px]"
            data-testid="partner-aij"
          >
            <img src={AIJ_LOGO} alt="Action Impact Jeunesse" className="max-h-24 object-contain" />
          </a>
          {partners.map((p) => {
            const Card = p.website ? "a" : "div";
            const props = p.website ? { href: p.website, target: "_blank", rel: "noreferrer" } : {};
            return (
              <Card
                key={p.id}
                {...props}
                className="uf-card p-6 flex items-center justify-center hover:border-[#FF6B1A] transition-colors min-h-[140px]"
                data-testid={`partner-${p.id}`}
              >
                <img src={p.logo_base64} alt={p.name} className="max-h-24 object-contain" />
              </Card>
            );
          })}
          {partners.length === 0 &&
            ["MAIRIE", "AMAQY", "IMPULSYON"].map((p) => (
              <div
                key={p}
                className="uf-card p-10 flex items-center justify-center hover:border-[#FF6B1A] transition-colors min-h-[140px]"
              >
                <span className="font-display text-2xl sm:text-3xl">{p}</span>
              </div>
            ))}
        </div>
      </section>
    </div>
  );
}

function DisciplineCard({ title, subtitle, img, features }) {
  return (
    <div className="uf-card overflow-hidden group" data-testid={`discipline-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="relative h-64 overflow-hidden">
        <img src={img} alt={title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <div className="absolute bottom-4 left-4">
          <p className="text-xs tracking-widest uppercase text-[#FF6B1A]">{subtitle}</p>
          <h3 className="font-display text-4xl">{title}</h3>
        </div>
      </div>
      <div className="p-6 flex flex-wrap gap-2">
        {features.map((f) => (
          <span key={f} className="text-xs tracking-widest uppercase border border-white/20 px-3 py-1">
            {f}
          </span>
        ))}
      </div>
    </div>
  );
}

function DayPreview({ title, items }) {
  return (
    <div className="uf-card p-6">
      <div className="flex items-center gap-3 mb-6">
        <span className="w-2 h-8 bg-[#FF6B1A]" />
        <h3 className="font-display text-3xl">{title}</h3>
      </div>
      <ul className="space-y-3">
        {items.map((it, i) => (
          <li key={i} className="flex gap-4 border-b border-white/10 pb-3 last:border-0">
            <span className="text-[#FF6B1A] font-mono text-sm whitespace-nowrap">{it.time}</span>
            <span className="text-white/80 text-sm">{it.activity}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
